import { Controller } from "@hotwired/stimulus"
import {
  createState,
  detect,
  energy,
  drawPlanet,
  canvasSize,
  parseOneliner,
  formatOneliner,
  DEFAULT_THRESHOLD,
  DEFAULT_BAND,
} from "../modules/planet_sim"

// "The line that moves the planet": a cheap 2D canvas pseudo-planet driven by the
// article's EXACT beat detector over a synthetic 120 BPM bench signal. Boots only
// via the power-latch (dg:power); the rAF loop pauses off-screen
// (IntersectionObserver) and on hidden tabs, and NEVER runs under reduced motion —
// there, POWER renders one static frame and each param edit re-renders one. Honest
// meters: FPS from real rAF deltas ("—" whenever no loop runs); THRESHOLD/BAND echo
// the live params; BEAT lights only when the detector actually fires.
//
// The sim + draw math lives in modules/planet_sim (pure, canvas-context-agnostic)
// so the loop and draw core are testable without a real 2D context.
export default class extends Controller {
  connect() {
    const el = this.element
    this.canvas = el.querySelector("canvas.bay-canvas")
    if (!this.canvas) return
    try { this.ctx = this.canvas.getContext("2d") } catch { this.ctx = null }

    this.input = el.querySelector('[data-role="oneliner"]')
    this.field = this.input?.closest(".field-oneliner")
    this.faders = {
      threshold: el.querySelector('[data-param="threshold"]'),
      band: el.querySelector('[data-param="band"]'),
    }
    this.vals = {
      threshold: el.querySelector('[data-val="threshold"]'),
      band: el.querySelector('[data-val="band"]'),
    }
    this.rFps = el.querySelector('[data-role="fps"]')
    this.rThr = el.querySelector('[data-role="r-threshold"]')
    this.rBand = el.querySelector('[data-role="r-band"]')
    this.lamp = el.querySelector(".beat-lamp")
    this.statusLed = el.querySelector('[data-role="status-led"]')

    this.state = createState()
    this.on = false
    this.visible = true
    this.raf = 0
    this.last = 0
    this.frames = 0
    this.fpsT = 0

    this._frame = this._frame.bind(this)
    this._onInput = this._onInput.bind(this)
    this._onFade = this._onFade.bind(this)
    this._onPower = this._onPower.bind(this)
    this._onReset = this._onReset.bind(this)
    this._onVis = this._onVis.bind(this)

    this.input?.addEventListener("input", this._onInput)
    this.faders.threshold?.addEventListener("input", this._onFade)
    this.faders.band?.addEventListener("input", this._onFade)
    el.addEventListener("dg:power", this._onPower)
    el.addEventListener("dg:reset", this._onReset)

    // Honest instrument: the loop halts off-screen and on hidden tabs.
    if (typeof IntersectionObserver === "function") {
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { this.visible = entry.isIntersecting; this._rearm() })
      }, { threshold: 0.05 })
      this._io.observe(el)
    }
    document.addEventListener("visibilitychange", this._onVis)

    this._sync(false)
  }

  disconnect() {
    if (!this.canvas) return
    cancelAnimationFrame(this.raf)
    this._io?.disconnect()
    document.removeEventListener("visibilitychange", this._onVis)
    this.element.removeEventListener("dg:power", this._onPower)
    this.element.removeEventListener("dg:reset", this._onReset)
    this.input?.removeEventListener("input", this._onInput)
    this.faders.threshold?.removeEventListener("input", this._onFade)
    this.faders.band?.removeEventListener("input", this._onFade)
  }

  // ---- the rAF loop, with an honest FPS meter ----
  _frame(now) {
    if (!this.on) return
    if (!this.visible || document.hidden) { this.raf = 0; return } // truly pause; re-armed on re-entry
    const { e, beat } = detect(this.state, Math.min(50, this.last ? now - this.last : 16.7))
    this._draw(e, beat)
    this.lamp?.classList.toggle("hit", beat)
    this.frames++
    if (now - this.fpsT >= 500) {
      if (this.rFps) this.rFps.textContent = String(Math.round(this.frames * 1000 / (now - this.fpsT)))
      this.frames = 0
      this.fpsT = now
    }
    this.last = now
    this.raf = requestAnimationFrame(this._frame)
  }

  // Re-arm after an off-screen / hidden pause — never under reduced motion.
  _rearm() {
    if (!this.on || this._reducedMotion() || this.raf || !this.visible || document.hidden) return
    this.last = 0
    this.frames = 0
    this.fpsT = performance.now()
    this.raf = requestAnimationFrame(this._frame)
  }

  _draw(e, beat) {
    if (this.ctx) drawPlanet(this.ctx, this.canvas.width, this.canvas.height, this.state, e, beat)
  }

  _size() {
    const { width, height } = canvasSize(this.canvas)
    this.canvas.width = width
    this.canvas.height = height
  }

  // Reduced motion: a single honest frame — params live, no clock, no FPS claim.
  _staticFrame() {
    this._size()
    this.state.lava = 0.55
    this._draw(energy(80, this.state.band), false)
    if (this.rFps) this.rFps.textContent = "—"
  }

  // ---- UI sync: one-liner <-> faders <-> bay readout ----
  _sync(fromInput) {
    if (this.faders.threshold) this.faders.threshold.value = this.state.threshold
    if (this.faders.band) this.faders.band.value = this.state.band
    if (this.vals.threshold) this.vals.threshold.textContent = this.state.threshold.toFixed(2)
    if (this.vals.band) this.vals.band.textContent = String(this.state.band)
    if (this.rThr) this.rThr.textContent = this.state.threshold.toFixed(2)
    if (this.rBand) this.rBand.textContent = "0–" + this.state.band
    if (!fromInput && this.input) this.input.value = formatOneliner(this.state.threshold, this.state.band)
    if (this.on && this._reducedMotion()) this._staticFrame()
  }

  _onInput() {
    const parsed = parseOneliner(this.input.value)
    if (parsed) {
      this.state.threshold = parsed.threshold
      this.state.band = parsed.band
      this.field?.classList.remove("err")
      this._sync(true)
    } else {
      this.field?.classList.add("err")
    }
  }

  _onFade() {
    this.state.threshold = parseFloat(this.faders.threshold?.value ?? this.state.threshold)
    this.state.band = parseInt(this.faders.band?.value ?? this.state.band, 10)
    this.field?.classList.remove("err")
    this._sync(false)
  }

  // ---- power / reset, from the power-latch ----
  _onPower(event) {
    this.on = !!event.detail?.on
    this.statusLed?.classList.toggle("on", this.on)
    if (!this.on) {
      cancelAnimationFrame(this.raf)
      this.raf = 0
      this.last = 0
      if (this.rFps) this.rFps.textContent = "—"
      this.lamp?.classList.remove("hit")
      return
    }
    if (this._reducedMotion()) { this._staticFrame(); return } // no loop, ever, under reduced motion
    this._size()
    this.state.t = 0
    this.state.rolling = 80
    this.state.lava = 0
    this.last = 0
    this.frames = 0
    this.fpsT = performance.now()
    this.raf = requestAnimationFrame(this._frame)
  }

  _onReset() {
    this.state.threshold = DEFAULT_THRESHOLD
    this.state.band = DEFAULT_BAND
    this._sync(false)
  }

  _onVis() {
    if (!document.hidden) { this.last = 0; this._rearm() }
  }

  _reducedMotion() {
    return typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
